/**
 * A brand colour made safe — `docs/proposal.md` §5.2.
 *
 * A company saves one hex in Settings and then every surface that uses it has
 * to decide, on its own, whether white text on it is readable, whether a
 * button in it looks like a button, and what a tint of it should be. Left to
 * each component that is six decisions and six answers; a page branded in
 * `#f2c230` ends up with gold text on white at 1.9 : 1, which nobody can read,
 * and a page branded in `#ffffff` ends up with an invisible button.
 *
 * **So one function decides, once, and the page uses only its answers.** The
 * logo keeps the company's exact file and the band keeps the company's exact
 * colour; what changes is the *text* and the *fill*, which are the two places
 * a colour can make a document unreadable. `brandPalette()` is that function
 * and the eight CSS variables it feeds are the only brand colour anything in
 * `client/src/quote/proposal/` is allowed to name (§5.3).
 *
 * The algorithm is the mockups' own, ported step for step so the page renders
 * the colours the boards were drawn in. `brand-palette.test.ts` holds its six
 * vectors to the hex digit, plus a 9,000-colour sweep that asserts the three
 * contrast guarantees rather than trusting that the steps imply them.
 */
/** The ink the design system uses for text, and the dark half of every pair. */
const INK = '#1d1d1a';
const WHITE = '#ffffff';
export const DEFAULT_BRAND = '#2e7d4f';
/** WCAG's threshold for body text, and for text on a fill. */
const TEXT_CONTRAST = 4.5;
/** WCAG's threshold for a non-text part: a border, a track, a focus ring. */
const UI_CONTRAST = 3;
// ---------------------------------------------------------------------------
// The colour arithmetic
// ---------------------------------------------------------------------------
const clamp = (value) => Math.min(255, Math.max(0, Math.round(value)));
const parse = (hex) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
];
const toHex = (rgb) => `#${rgb.map((one) => clamp(one).toString(16).padStart(2, '0')).join('')}`;
/**
 * WCAG relative luminance.
 *
 * The threshold is **0.03928**, which is WCAG 2.0's published figure and the
 * one the mockups were drawn with. The sRGB specification's own number is
 * 0.04045 and WCAG 2.1 corrected to it; the two differ only for a channel at
 * exactly 10/255, and the vectors in the test were generated with this one.
 * Named here rather than inlined so there is one threshold in the repo and it
 * is findable.
 */
export function relativeLuminance(hex) {
    const channel = (value) => {
        const c = value / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const [r, g, b] = parse(hex);
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}
/** The WCAG ratio between two colours, 1 (identical) to 21 (black on white). */
export function contrast(a, b) {
    const la = relativeLuminance(a);
    const lb = relativeLuminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
/** `a` moved `t` of the way toward `b`, per channel. */
export function mix(a, b, t) {
    const from = parse(a);
    const to = parse(b);
    return toHex([
        from[0] + (to[0] - from[0]) * t,
        from[1] + (to[1] - from[1]) * t,
        from[2] + (to[2] - from[2]) * t,
    ]);
}
// ---------------------------------------------------------------------------
// The palette
// ---------------------------------------------------------------------------
export function brandPalette(hex) {
    // 1. A colour we cannot read is the default green, not a crash.
    const brand = /^#[0-9a-f]{6}$/i.test(hex) ? hex.toLowerCase() : DEFAULT_BRAND;
    /*
     * 4. The text-safe brand: darkened in 3% steps until white can sit behind
     * it at 4.5 : 1. Stepping rather than solving, because the step size is what
     * keeps a gold at #8c711c rather than at the mathematically minimal shade —
     * the mockups' gold, and a colour that still reads as the company's.
     */
    let text = brand;
    let t = 0;
    while (contrast(text, WHITE) < TEXT_CONTRAST && t < 0.95) {
        t += 0.03;
        text = mix(brand, '#000000', t);
    }
    /*
     * 5. The fill is the saved colour whenever *something* can be written on
     * it — white or ink. That is the common case and it is why a dark navy and
     * a bright gold both keep their exact colour on the button.
     */
    const whiteOnBrand = contrast(WHITE, brand);
    const inkOnBrand = contrast(INK, brand);
    let fill = brand;
    let on = WHITE;
    let darkened = false;
    if (Math.max(whiteOnBrand, inkOnBrand) >= TEXT_CONTRAST) {
        on = whiteOnBrand >= TEXT_CONTRAST ? WHITE : INK;
    }
    else {
        fill = text;
        on = WHITE;
        darkened = true;
    }
    /*
     * 6. The near-white guard. A brand that is almost white cannot be a fill at
     * all: the button disappears into the page and the band is a white stripe on
     * a white page. The fill becomes the text-safe shade and the band is forced
     * off, because there is nothing to draw it in.
     */
    let nearWhite = false;
    if (contrast(fill, WHITE) < 1.5) {
        fill = text;
        on = WHITE;
        darkened = fill !== brand;
        nearWhite = true;
    }
    // 7. A non-text part needs only 3 : 1, so it keeps the fill more often.
    const ui = contrast(fill, WHITE) >= UI_CONTRAST ? fill : text;
    return {
        brand,
        fill,
        on,
        text,
        ui,
        tint1: mix(WHITE, fill, 0.07),
        tint2: mix(WHITE, fill, 0.14),
        line: mix(WHITE, fill, 0.38),
        ring: mix(WHITE, fill, 0.26),
        contour: mix(fill, on, 0.13),
        darkened,
        nearWhite,
        ratios: {
            whiteOnBrand,
            inkOnBrand,
            textOnWhite: contrast(text, WHITE),
            onOnFill: contrast(on, fill),
        },
    };
}
/**
 * The eight variables, ready for a `style` attribute.
 *
 * Components read `var(--b)` and friends and never the hex, which is what
 * makes the "no `#` colours inside the proposal module" rule checkable (§13).
 */
export function brandVars(palette) {
    return {
        '--b': palette.fill,
        '--on': palette.on,
        '--tx': palette.text,
        '--ui': palette.ui,
        '--t1': palette.tint1,
        '--t2': palette.tint2,
        '--ln': palette.line,
        '--ring': palette.ring,
        '--contour': palette.contour,
    };
}
/**
 * What the Brand card says about a colour, in a sentence (§10.1 card 4).
 *
 * The office needs to know *whether we changed anything*, because a brand
 * guardian who sees a slightly different green on a proposal and is not told
 * why will file it as a bug. So the sentence names the number and says the
 * logo is untouched.
 */
export function brandNote(palette) {
    const said = (ratio) => `${Math.floor(ratio * 10) / 10} : 1`;
    if (palette.nearWhite) {
        return `Your colour is too light to use as a fill, so buttons and labels on `
            + `this page use ${palette.fill} and the cover band is off. Your logo is `
            + `not changed.`;
    }
    if (palette.darkened) {
        return `Your colour is a little light for text. We darken it to `
            + `${palette.text} on this page. Your logo is not changed.`;
    }
    const pair = palette.on === WHITE ? 'White text' : 'Dark text';
    return `${pair} on the brand colour: ${said(palette.ratios.onOnFill)}. `
        + `No adjustment needed.`;
}
//# sourceMappingURL=brand-palette.js.map